import { computed, inject, signal } from '@angular/core';
import { SearchParamsService } from '@educa/educa-ui';
import { SortingState } from '@tanstack/angular-table';

export enum FilterType {
  OR = 'or',
  AND = 'and'
}

export interface FilterOption {
  value: string;
  type: FilterType;
}

export type FilterConfig = Record<string, FilterOption>;

export class ListService {
  private searchParamsService = inject(SearchParamsService);

  readonly sorting = signal<SortingState>([]);
  readonly filtersString = this.searchParamsService.getParam('filters');
  readonly sortingString = this.searchParamsService.getParam('sort');
  readonly search = this.searchParamsService.getParam('search');
  readonly pageParam = computed(() => {
    const pageQueryString = this.searchParamsService.getParam('page')();

    return pageQueryString ? parseInt(pageQueryString) : 0;
  });
  readonly limit = signal<number>(15);

  /**
   * To control whether the query
   */
  protected readonly isQueryActive = signal(false);

  public rowsPerPageText = $localize`Rows per page:`;

  setQueryActive(active: boolean): void {
    this.isQueryActive.set(active);
  }

  nextPage(): void {
    const nextPage = this.pageParam() + 1;
    this.searchParamsService.setParams({ page: nextPage });
  }

  previousPage(): void {
    const prevPage = Math.max(0, this.pageParam() - 1);
    this.searchParamsService.setParams({ page: prevPage });
  }

  firstPage(): void {
    this.searchParamsService.setParams({ page: 0 });
  }

  lastPage(total: number): void {
    const lastPage = Math.max(0, Math.ceil(total / this.limit()) - 1);
    this.searchParamsService.setParams({ page: lastPage });
  }

  onSortingChange(sorting: SortingState): void {
    this.searchParamsService.setParams({
      sort: sorting
        .map((sort) => {
          const operator = sort.desc ? '-' : '+';

          return `${operator}${sort.id}`;
        })
        .join('|')
    });
    this.sorting.set(sorting);
  }

  protected buildFilters(
    searchArray: string[],
    filtersString: string,
    filterConfig: FilterConfig,
    advancedFilters: Record<string, any> = {},
    quickFilterOption?: string
  ): { filter_or?: string[]; filter?: string[] } {
    const filter_or: string[] = [];
    const filter_and: string[] = [];

    // Process filter configuration
    for (const [key, options] of Object.entries(filterConfig)) {
      if (advancedFilters[key] || filtersString.includes(key)) {
        if (options.type === FilterType.OR) {
          filter_or.push(options.value);
        } else {
          filter_and.push(options.value);
        }
      }
      if (quickFilterOption && quickFilterOption === key) {
        filter_and.push(options.value);
      }
    }

    // Add search filters
    if (searchArray.length > 0) {
      filter_or.push(...searchArray);
    }

    return {
      filter_or: filter_or.length > 0 ? filter_or : undefined,
      filter: filter_and.length > 0 ? filter_and : undefined
    };
  }

  protected getQueryParams({
    page,
    limit,
    sort,
    filters
  }: {
    page?: string | number | string[];
    limit?: string | number | string[];
    sort?: string | number | string[];
    filters?: { filter_or?: string[]; filter?: string[] };
  }): Record<string, unknown> {
    return {
      page: (page ?? 0) as number,
      limit: (limit ?? 15) as number,
      sort: sort || undefined,
      ...filters
    };
  }

  public setLimit(limit: number): void {
    let page: number;
    if (limit != this.limit()) {
      page = Math.floor((this.pageParam() * this.limit()) / limit);
    } else {
      page = this.pageParam();
    }
    this.searchParamsService.setParams({ page: page, limit: limit });
    this.limit.set(limit);
  }
}
